import TextCopyToClipboard from '@components/TextCopyToClipboard';
import { HoverBorderButton, PrimaryLink } from '@elements/Button';
import { DangerText, MuteText } from '@elements/Common';
import { NumberMaskInput } from '@elements/CustomMaskInput';
import { DarkPopup } from '@elements/Popup';
import { ResponsiveDotts } from '@page-components/dashboard/common';
import { CopySimple, Plus, Trash } from '@phosphor-icons/react';
import { CommonUtility } from '@utility/common';
import {
  cloneElement,
  Fragment,
  type HTMLAttributes,
  type ReactNode,
  isValidElement,
  useEffect,
  useMemo,
} from 'react';
import { cn } from '@skupreme/shared/ui';
import { FIELD_INPUT_INVALID } from '@elements/form-field/styles';
import { Flag } from '@elements/Flag';
import { AppTable } from '@components/AppTable';
import { ItemImage } from '@components/CatalogElements';
import { NarfConatiner, NarfTag, Row } from './ItemsCommon';
import { LoaderBar } from '@components/LoaderBar';
import { ItemMiniInput } from './list/common';
import type { ShipmentDetailItemType } from 'types/shipment-inbound/common';
import type { ShipmentCreateItem } from 'types/shipment-inbound/create';
import type { shipmentBoxInformationType } from '@hooks/shipment';
import type { ITableColumn } from 'types/common/table';
import type { ShipmentItemUserValue, ShipmentUserValueType } from './common';

type DivProps = HTMLAttributes<HTMLDivElement>;

export function BoxesRowContainer({ className, ...rest }: DivProps) {
  return (
    <div
      {...rest}
      className={cn(
        'sk:flex sk:w-fit sk:min-w-full sk:bg-surface sk:py-2.5',
        'sk:border-0 sk:border-b sk:border-solid sk:border-rule',
        'sk:[&_.border]:absolute sk:[&_.border]:-top-2.5 sk:[&_.border]:left-0 sk:[&_.border]:h-[calc(100%+20px)] sk:[&_.border]:w-0.75 sk:[&_.border]:border-0 sk:[&_.border]:bg-border',
        className,
      )}
    />
  );
}

type FixWidthProps = DivProps & {
  width?: number;
  leftPos?: number;
  textAlign?: string;
};

export function FixWidth({
  width,
  leftPos,
  textAlign: _textAlign,
  className,
  style,
  ...rest
}: FixWidthProps) {
  return (
    <div
      {...rest}
      style={{
        maxWidth: width || 0,
        minWidth: width || 0,
        left: leftPos,
        ...style,
      }}
      className={cn(
        'sk:bg-surface',
        'sk:[&.flex-center]:flex sk:[&.flex-center]:items-center sk:[&.flex-center]:justify-center',
        'sk:[&.sticky]:sticky sk:[&.sticky]:z-2',
        className,
      )}
    />
  );
}

function ShowIconsOnHover({ className, ...rest }: DivProps) {
  return (
    <div
      {...rest}
      className={cn(
        'sk:h-3.75 sk:[&_.icon]:hidden sk:[&_.icon]:cursor-pointer sk:hover:[&_.icon]:block',
        className,
      )}
    />
  );
}

const maskOptions = {
  allowDecimal: true,
  decimalSymbol: '.',
  decimalLimit: 2,
};

export function BoxesColumn({ column }) {
  return (
    <FixWidth
      className={`${column.className || ''} ${column.sticky ? 'sticky' : ''}`}
      leftPos={column.leftPos}
      width={column.width}
    >
      {isValidElement(column.component) ? (
        cloneElement(column.component)
      ) : (
        <MuteText className="sk:px-5!">{column.text}</MuteText>
      )}
    </FixWidth>
  );
}

function MiniInput({
  error,
  children,
}: {
  error?: boolean;
  children: (invalidClass: string | undefined) => ReactNode;
}) {
  return (
    <div className="sk:w-17.5">
      {children(error ? FIELD_INPUT_INVALID : undefined)}
    </div>
  );
}

type AddBoxesRowProps = {
  boxesInformation: shipmentBoxInformationType[];
  imperial?: boolean;
  addBox: () => void;
  changeBox: (index: number, key: string, value: any) => void;
  columns: any;
  errors?: Record<string, Record<string, string>>;
  setIsChanged?: (isChanged: boolean) => void;
};

export function AddBoxesRow({
  boxesInformation,
  imperial,
  addBox,
  changeBox,
  columns,
  errors,
  setIsChanged,
}: AddBoxesRowProps) {
  const boxChange = (index, key, value) => {
    changeBox(index, key, value);
    if (CommonUtility.isFunction(setIsChanged)) {
      setIsChanged?.(true);
    }
  };

  return (
    <BoxesRowContainer>
      <FixWidth
        className={`sk:px-5! ${columns[0]?.sticky ? 'sticky' : ''}`}
        width={columns[0]?.width}
        leftPos={columns[0].leftPos}
      >
        <div>
          <div className="sk:pb-5!">
            <b>Box Properties</b>
          </div>
          <HoverBorderButton
            type="button"
            onClick={addBox}
            className="sk:mt-5! flex-btn"
          >
            <Plus className="sk:mr-1!" /> Box
          </HoverBorderButton>
        </div>
      </FixWidth>
      {!!boxesInformation.length && (
        <FixWidth
          className={`sk:px-5! ${columns[1]?.sticky ? 'sticky' : ''}`}
          width={columns[1]?.width}
          leftPos={columns[1].leftPos}
        />
      )}
      <FixWidth
        className={`sk:px-5! ${columns[2]?.sticky ? 'sticky' : ''}`}
        width={columns[2]?.width}
        leftPos={columns[2].leftPos}
      />
      {!!boxesInformation.length && (
        <div className="sk:flex! sk:flex-col! sk:gap-y-1.25">
          <div className="sk:flex!">
            <FixWidth
              className={`sk:h-full sk:py-1! sk:px-5! ${
                columns[3]?.sticky ? 'sticky' : ''
              }`}
              width={columns[3]?.width}
              leftPos={columns[3].leftPos}
            >
              <MuteText>Weight ({imperial ? 'lb' : 'kg'})</MuteText>
            </FixWidth>
            {boxesInformation.map((box, index) => (
              <FixWidth
                className="sk:px-5!"
                width={columns[4 + index]?.width}
                textAlign="center"
              >
                <MiniInput error={!!errors?.[`box-${index}`]?.boxGrossWeight}>
                  {invalidClass => (
                    <NumberMaskInput
                      onChange={value =>
                        boxChange(index, 'boxGrossWeight', value)
                      }
                      value={box.boxGrossWeight?.value}
                      maskOptions={maskOptions}
                      className={invalidClass}
                    />
                  )}
                </MiniInput>
                {!!errors?.[`box-${index}`]?.boxGrossWeight && (
                  <DangerText>
                    {errors?.[`box-${index}`]?.boxGrossWeight}
                  </DangerText>
                )}
              </FixWidth>
            ))}
          </div>
          <div className="sk:flex!">
            <FixWidth
              className={`sk:h-full sk:py-1! sk:px-5! ${
                columns[3]?.sticky ? 'sticky' : ''
              }`}
              width={columns[3]?.width}
              leftPos={columns[3].leftPos}
            >
              <MuteText>Length ({imperial ? 'in' : 'cm'})</MuteText>
            </FixWidth>
            {boxesInformation.map((box, index) => (
              <FixWidth
                className="sk:px-5!"
                width={columns[4 + index]?.width}
                textAlign="center"
              >
                <MiniInput error={!!errors?.[`box-${index}`]?.boxLength}>
                  {invalidClass => (
                    <NumberMaskInput
                      onChange={value => boxChange(index, 'boxLength', value)}
                      value={box.boxLength?.value}
                      maskOptions={maskOptions}
                      className={invalidClass}
                    />
                  )}
                </MiniInput>
                {!!errors?.[`box-${index}`]?.boxLength && (
                  <DangerText>{errors?.[`box-${index}`]?.boxLength}</DangerText>
                )}
              </FixWidth>
            ))}
          </div>
          <div className="sk:flex!">
            <FixWidth
              className={`sk:h-full sk:py-1! sk:px-5! ${
                columns[3]?.sticky ? 'sticky' : ''
              }`}
              width={columns[3]?.width}
              leftPos={columns[3].leftPos}
            >
              <MuteText>Width ({imperial ? 'in' : 'cm'})</MuteText>
            </FixWidth>
            {boxesInformation.map((box, index) => (
              <FixWidth
                className="sk:px-5!"
                width={columns[4 + index]?.width}
                textAlign="center"
              >
                <MiniInput error={!!errors?.[`box-${index}`]?.boxWidth}>
                  {invalidClass => (
                    <NumberMaskInput
                      onChange={value => boxChange(index, 'boxWidth', value)}
                      value={box.boxWidth?.value}
                      maskOptions={maskOptions}
                      className={invalidClass}
                    />
                  )}
                </MiniInput>
                {!!errors?.[`box-${index}`]?.boxWidth && (
                  <DangerText>{errors?.[`box-${index}`]?.boxWidth}</DangerText>
                )}
              </FixWidth>
            ))}
          </div>
          <div className="sk:flex!">
            <FixWidth
              className={`sk:h-full sk:py-1! sk:px-5! ${
                columns[3]?.sticky ? 'sticky' : ''
              }`}
              width={columns[3]?.width}
              leftPos={columns[3].leftPos}
            >
              <MuteText>Height ({imperial ? 'in' : 'cm'})</MuteText>
            </FixWidth>
            {boxesInformation.map((box, index) => (
              <FixWidth
                className="sk:px-5!"
                width={columns[4 + index]?.width}
                textAlign="center"
              >
                <MiniInput error={!!errors?.[`box-${index}`]?.boxHeight}>
                  {invalidClass => (
                    <NumberMaskInput
                      onChange={value => boxChange(index, 'boxHeight', value)}
                      value={box.boxHeight?.value}
                      maskOptions={maskOptions}
                      className={invalidClass}
                    />
                  )}
                </MiniInput>
                {!!errors?.[`box-${index}`]?.boxHeight && (
                  <DangerText>{errors?.[`box-${index}`]?.boxHeight}</DangerText>
                )}
                {!!errors?.[`box-${index}`]?.boxError && (
                  <DangerText>{errors?.[`box-${index}`]?.boxError}</DangerText>
                )}
              </FixWidth>
            ))}
          </div>
        </div>
      )}
    </BoxesRowContainer>
  );
}

type useUnitViewHookParams = {
  boxesInformation: shipmentBoxInformationType[];
  deleteBoxColumn: (index: number) => void;
  duplicateBoxColumn: (index: number) => void;
};

export type BoxInformationColumnType = Omit<ITableColumn, 'width'> & {
  width: number;
  leftPos?: number;
  sticky?: boolean;
};

type useUnitViewHookReturn = {
  columns: BoxInformationColumnType[];
};

export const useUnitViewHook = ({
  boxesInformation,
  deleteBoxColumn,
  duplicateBoxColumn,
}: useUnitViewHookParams): useUnitViewHookReturn => {
  const columns = useMemo(() => {
    let arr: BoxInformationColumnType[] = [
      {
        text: 'Product Information',
        width: 150,
        key: 'image',
        sticky: true,
      },
      {
        text: '',
        component: <div className="sk:mx-5! sk:px-5!" />,
        width: 300,
        key: 'info',
        sticky: true,
      },
      {
        text: 'Units Boxes',
        key: 'units_boxed',
        width: 150,
        sticky: true,
        className: 'sk:text-center',
      },
    ];

    if (CommonUtility.isValidArray(boxesInformation)) {
      arr.push({
        text: '',
        key: 'dimension',
        width: 130,
        sticky: true,
      });

      boxesInformation.forEach((box, index) => {
        arr.push({
          text: `Box ${index + 1}`,
          component: (
            <ShowIconsOnHover className="sk:flex! sk:items-center! sk:justify-center! sk:mt-1!">
              <MuteText>Box {index + 1}</MuteText>
              <DarkPopup
                trigger={
                  <Trash
                    onClick={() => deleteBoxColumn(index)}
                    size={15}
                    className="icon"
                  />
                }
                position="top center"
              >
                Delete Box
              </DarkPopup>
              <DarkPopup
                trigger={
                  <CopySimple
                    onClick={() => duplicateBoxColumn(index)}
                    size={15}
                    className="icon"
                  />
                }
                position="top center"
              >
                Duplicate Box
              </DarkPopup>
            </ShowIconsOnHover>
          ),
          key: `box-${index}`,
          width: 100,
        });
      });
    }

    arr.push({
      text: 'Received Qty',
      key: 'received_qty',
      width: 150,
    });

    let width = 0;
    arr = arr.map(item => {
      if (item.sticky) {
        item.leftPos = width;
        width += item.width;
      }
      return item;
    });
    return arr;
  }, [boxesInformation]);

  return { columns };
};

export function TableContainer({ className, ...rest }: DivProps) {
  return (
    <div
      {...rest}
      className={cn(
        'sk:relative sk:my-2.5 sk:overflow-x-auto sk:rounded-t-[5px] sk:shadow-card',
        'sk:[&.no-shadow]:shadow-none!',
        className,
      )}
    />
  );
}

export function BoxesColumnContainer({ className, ...rest }: DivProps) {
  return (
    <div
      {...rest}
      className={cn(
        'sk:flex sk:w-fit sk:min-w-full sk:bg-surface sk:py-2.5',
        'sk:border-0 sk:border-b-2 sk:border-solid sk:border-border',
        'sk:[&_span]:text-xs',
        className,
      )}
    />
  );
}

type ComignledBoxRowProps = {
  index: number;
  isUnitView: boolean;
  imperial: boolean;
  item: ShipmentDetailItemType;
  boxesInformation: shipmentBoxInformationType[];
  addBox: () => void;
  changeBox: (index: number, key: string, value: any) => void;
  columns: any;
  updatedValue: {
    [itemId: string]: ShipmentItemUserValue;
  };
  inputChanged: (item: any, key: string, value: any) => void;
  setUnitViewError: (unitViewError: any) => void;
  setIsChanged: (isChanged: boolean) => void;
  unitViewError: any;
};

function ComignledBoxRow({
  index,
  isUnitView,
  imperial,
  item,
  boxesInformation,
  addBox,
  changeBox,
  columns,
  updatedValue,
  inputChanged,
  setUnitViewError,
  setIsChanged,
  unitViewError,
}: ComignledBoxRowProps) {
  const idKey = 'itemId';
  const _id = item[idKey];

  const is_NARF = false;
  const itemValue = useMemo(() => updatedValue[_id] || {}, [updatedValue, _id]);

  const inputtedItems = useMemo(() => {
    let total = 0;
    boxesInformation.forEach(box => {
      if (!isNaN(itemValue?.[`box-${box._id}`])) {
        total += Number(itemValue?.[`box-${box._id}`]);
      }
    });
    return total;
  }, [itemValue, boxesInformation]);

  const onChange = (key, value) => {
    inputChanged(
      {
        ...item,
        _id,
      },
      key,
      value,
    );
  };

  const isMultiple = useMemo(() => {
    let variantsAvailable = 0;
    item.aliases.forEach(variant => {
      if (itemValue?.[variant.alias]) {
        variantsAvailable += 1;
      }
    });
    return variantsAvailable > 1;
  }, [item]);

  const maxItems = useMemo(() => {
    let maxQty = 0;
    item.aliases.forEach(variant => {
      if (itemValue?.[variant.alias]) {
        maxQty += itemValue[variant.alias];
      }
    });

    return maxQty || item.qty;
  }, [isMultiple]);

  useEffect(() => {
    if (!inputtedItems || !maxItems) return;

    setUnitViewError(unitViewError => ({
      ...unitViewError,
      [_id]: !!(inputtedItems > maxItems),
    }));
  }, [inputtedItems, maxItems]);

  return (
    <>
      {index === 0 && isUnitView && (
        <AddBoxesRow
          boxesInformation={boxesInformation}
          imperial={imperial}
          addBox={addBox}
          changeBox={changeBox}
          setIsChanged={setIsChanged}
          columns={columns}
          errors={unitViewError}
        />
      )}
      <BoxesRowContainer>
        <FixWidth
          className={`sk:px-5! ${columns[0]?.sticky ? 'sticky' : ''}`}
          width={columns[0]?.width}
          leftPos={columns[0].leftPos}
        >
          <NarfConatiner className={is_NARF ? 'disabled-image' : ''}>
            <ItemImage
              item={{
                p_images: {
                  display_image: item.image,
                },
              }}
              noMargin
            />
            {is_NARF && <NarfTag>NARF</NarfTag>}
          </NarfConatiner>
        </FixWidth>
        <FixWidth
          className={`sk:px-5! ${columns[1]?.sticky ? 'sticky' : ''}`}
          width={columns[1]?.width}
          leftPos={columns[1].leftPos}
        >
          <Row>
            <a href={`/app/catalogs/catalog/${_id}`}>{item.title}</a>
          </Row>
          <Row>
            <MuteText>SKU</MuteText>
            <ResponsiveDotts>
              <TextCopyToClipboard text={item.sku} iconSize={16} />
            </ResponsiveDotts>
          </Row>
          {item.asin && (
            <Row>
              <MuteText>ASIN</MuteText>
              <ResponsiveDotts>
                <TextCopyToClipboard text={item.asin} iconSize={16} />
              </ResponsiveDotts>
            </Row>
          )}
        </FixWidth>
        <FixWidth
          className={`sk:px-5! ${
            columns[2]?.sticky ? 'sticky' : ''
          } flex-center sk:flex-col!`}
          width={columns[2]?.width}
          leftPos={columns[2].leftPos}
        >
          {inputtedItems}/{maxItems}
          {!!(inputtedItems > maxItems) && (
            <DangerText className="sk:text-center">
              The selected quantity exceeds the maximum allowable units
            </DangerText>
          )}
        </FixWidth>
        {!!boxesInformation.length && (
          <FixWidth
            className={`sk:px-5! ${columns[3]?.sticky ? 'sticky' : ''}`}
            width={columns[3]?.width}
            leftPos={columns[3].leftPos}
          />
        )}
        {boxesInformation?.map(box => (
          <FixWidth width={columns[4]?.width} className="flex-center sk:px-5!">
            {/* @ts-expect-error - TS2741: Property 'maxQty' is missing in type '{ value: any; onChange: (value: any) => vo... */}
            <ItemMiniInput
              value={itemValue?.[`box-${box._id}`]}
              onChange={value => onChange(`box-${box._id}`, value)}
              noMax
              maskOptions={maskOptions}
            />
          </FixWidth>
        ))}
        <FixWidth
          width={columns[columns.length - 1]?.width}
          className="flex-center sk:px-5!"
        >
          {item.receivedQty}
          <MuteText className="sk:ml-1!">Units</MuteText>
        </FixWidth>
      </BoxesRowContainer>
    </>
  );
}

type originItemsType = {
  _id: string;
  items: ShipmentDetailItemType[];
}[];

type UnitViewItemListProps = {
  addBox: () => void;
  boxesInformation: shipmentBoxInformationType[];
  changeBox: (index: number, key: string, value: any) => void;
  deleteBoxColumn: (index: number) => void;
  duplicateBoxColumn: (index: number) => void;
  imperial: boolean;
  inputChanged: (
    id: string,
    item: ShipmentCreateItem,
    name: string,
    value: any,
  ) => void;
  isUnitView: boolean;
  isPOShipment: boolean;
  items: originItemsType;
  originList: any;
  setIsChanged: (isChanged: boolean) => void;
  setUnitViewError: (unitViewError: any) => void;
  userValue: ShipmentUserValueType;
  unitViewError: any;
  loading: boolean;
};

export function UnitViewItemList({
  addBox,
  boxesInformation,
  changeBox,
  deleteBoxColumn,
  duplicateBoxColumn,
  imperial,
  inputChanged,
  isPOShipment,
  isUnitView,
  items,
  loading,
  originList,
  setIsChanged,
  setUnitViewError,
  userValue,
  unitViewError,
}: UnitViewItemListProps) {
  const { columns } = useUnitViewHook({
    boxesInformation,
    deleteBoxColumn,
    duplicateBoxColumn,
  });

  function POHeader({ originId }) {
    const POItem = useMemo(
      () => originList.find(x => [x.value, x._id].includes(originId)),
      [originList, originId],
    );

    return (
      <div className="sk:flex! sk:justify-between! sk:items-center!">
        <div className="sk:flex! sk:items-center!">
          <Flag width={30} height={20} name={POItem?.item.region_name} />
          <h3 className="sk:m-0! sk:ml-2!">{POItem?.item.name}</h3>
        </div>
        <PrimaryLink href={`/app/scm/purchase-orders/${POItem?.item._id}`}>
          {POItem?.item.display_id}
        </PrimaryLink>
      </div>
    );
  }

  return (
    <>
      {items?.length > 0 ? (
        items.map(origin => (
          <Fragment key={origin._id}>
            {isPOShipment && <POHeader originId={origin._id} />}
            <TableContainer>
              {loading && <LoaderBar />}
              <BoxesColumnContainer>
                {columns?.map(column => (
                  <BoxesColumn column={column} />
                ))}
              </BoxesColumnContainer>
              {origin?.items?.map((item, index) => (
                <ComignledBoxRow
                  isUnitView={isUnitView}
                  boxesInformation={boxesInformation}
                  imperial={imperial}
                  addBox={addBox}
                  changeBox={changeBox}
                  setIsChanged={setIsChanged}
                  item={item}
                  index={index}
                  setUnitViewError={setUnitViewError}
                  columns={columns}
                  inputChanged={(item, name, value) =>
                    inputChanged(origin._id, item, name, value)
                  }
                  updatedValue={userValue?.[origin._id] || []}
                  unitViewError={unitViewError}
                />
              ))}
            </TableContainer>
          </Fragment>
        ))
      ) : (
        <AppTable
          sticky
          data={[]}
          noDataMessage="No Data Found"
          // @ts-expect-error - TS2322: Type '({ text: string; width: number; key: string; sticky: boolean; component?: ...
          columns={columns}
        />
      )}
    </>
  );
}
